import {chemicalBaseTypes, propertyTypes, systemTypes, seasonTypes} from 'helpers/constants';

export const usPartialDrains = ({dealer, branch, analysis, products, pool, formatMessage, sanitizerType}) => {
  const naturalChemistyEnabled = !!branch && !!branch.naturalChemistry;
  let pHBuffer = '';

  //if is California
  let borateMessage = dealer.state === 'CA' ? 'scanning.partial.drain.borateCalifornia' : 'scanning.partial.drain.borate';

  if (products.length > 0 && pool && pool.pHBuffer) {
    let buffers = products.filter(x => x.id === pool.pHBuffer);

    if (buffers.length > 0) {
      pHBuffer = buffers[0].name;
    }
  }

  // [ALEX-2903] - Added a condition where the oxidizer values must have been entered before showing the popup.
  // It was possible to submit the form when selecting one of the popup options without having those values causing
  // the recommendation to be skipped.
  const hasOxidizerValues = analysis.freeChlorine && analysis.totalChlorine || analysis.totalBromine;

  if (!hasOxidizerValues) {
    return {
      partialDrainVisible: false,
      partialDrainMessage: '',
    };
  }

  if (
    [
      chemicalBaseTypes.brominePool,
      chemicalBaseTypes.chlorinePool,
      chemicalBaseTypes.mineralSpringsPool,
      chemicalBaseTypes.saltWaterPool,
    ].indexOf(sanitizerType) !== -1 && (
      analysis.cYA > 100 && !analysis.startup && pool.property === 'C' ||
      analysis.cYA > 200 && !analysis.startup && pool.property === 'R' ||
      analysis.cYA > 150 && analysis.startup && pool.property === 'R' ||
      analysis.cYA > 50 && analysis.startup && pool.property === 'C' ||
      analysis.cYA > 30 && sanitizerType === chemicalBaseTypes.brominePool)) {
    const ncCYARemoverDrainEnabled = !!naturalChemistyEnabled && !!dealer.cyaRemover &&
        analysis.season !== seasonTypes.closing && pool.typeID !== systemTypes.spa;

    return {
      partialDrainVisible: true,
      ncDrainEnabled: ncCYARemoverDrainEnabled,
      partialDrainMessage: ncCYARemoverDrainEnabled ? formatMessage({id: 'scanning.nc.partial-drain.cya'}) : formatMessage({id: 'scanning.partial.drain.cya'}),
    };
  }

  if (sanitizerType === chemicalBaseTypes.mineralSpringsPool && analysis.optimizerPlusBorate > 50 && analysis.startup ||
    analysis.optimizerPlusBorate > 90) {
    return {
      partialDrainVisible: true,
      partialDrainMessage: formatMessage({id: borateMessage}),
    };
  }

  if (sanitizerType === chemicalBaseTypes.mineralSpringsPool && analysis.minerals < 2600 && !analysis.startup) {
    let beginnings = (pool.idealSaltPPM - analysis.minerals) / 108 * pool.volume;
    let borateIncrease = Math.ceil(beginnings / pool.volume * 1.15);
    let newBorate = borateIncrease + parseFloat(analysis.optimizerPlusBorate);

    if (newBorate > 90) {
      return {
        partialDrainVisible: true,
        partialDrainMessage: formatMessage({id: borateMessage}),
      };
    }
  }

  if (sanitizerType === chemicalBaseTypes.mineralSpringsPool && pool.idealSaltPPM &&
    analysis.minerals > pool.idealSaltPPM * 1.1) {
    return {
      partialDrainVisible: true,
      partialDrainMessage: formatMessage({id: 'scanning.partial.drain.minerals'}),
    };
  }

  if (analysis.tDS > 2500 && pool.volume > 1000 && sanitizerType !== chemicalBaseTypes.mineralSpringsPool &&
    sanitizerType !== chemicalBaseTypes.saltWaterPool && pool.typeID !== systemTypes.spa) {
    return {
      partialDrainVisible: true,
      partialDrainMessage: formatMessage({id: 'scanning.partial.drain.tds'}),
    };
  }

  if (analysis.tDS > 2500 && pool.volume > 1000 && sanitizerType !== chemicalBaseTypes.mineralSpringsPool &&
    sanitizerType !== chemicalBaseTypes.saltWaterPool && pool.typeID !== systemTypes.spa) {
    return {
      partialDrainVisible: true,
      partialDrainMessage: formatMessage({id: 'scanning.complete.drain.tds'}),
    };
  }

  // Spa drains
  if ([chemicalBaseTypes.chlorineSpa, chemicalBaseTypes.bromineSpa, chemicalBaseTypes.softSoakSpa, chemicalBaseTypes.saltWaterSpa].indexOf(sanitizerType) !== -1) {
    if (analysis.tDS > 2500 && sanitizerType !== chemicalBaseTypes.saltWaterSpa) {
      return {
        partialDrainVisible: true,
        partialDrainMessage: formatMessage({id: 'scanning.tds.too.high'}),
      };
    }

    if (pool.property === propertyTypes.commercial && analysis.cYA > 100 ||
      pool.property === propertyTypes.residential && analysis.cYA > 200) {
      return {
        partialDrainVisible: true,
        partialDrainMessage: formatMessage({id: 'scanning.cya.too.high'}),
      };
    }
  }

  if (analysis.cDDistStandard !== null && analysis.cDDistStandard !== undefined && isFinite(analysis.cDDistStandard) &&
    (analysis.cDSample !== null && analysis.cDSample !== undefined && isFinite(analysis.cDSample))) {
    let cdDiff = analysis.cDDistStandard - analysis.cDSample;

    if (parseInt(analysis.testType) === 1 && cdDiff > 3 || parseInt(analysis.testType) === 2 && cdDiff * 14.8 > 100) {
      return {
        partialDrainVisible: true,
        partialDrainMessage: formatMessage({id: 'scanning.partial.drain.chlorine'}),
      };
    }

    if (pool.volume <= 2500 && cdDiff > 0) {
      return {
        partialDrainVisible: true,
        partialDrainMessage: formatMessage({id: 'scanning.complete.drain.chlorine'}),
      };
    }
  }

  if (pHBuffer === 'Spa Sentry' && !analysis.spaSentryStartup && analysis.pH > 7.6 && analysis.hardness >= 50) {
    return {
      partialDrainVisible: true,
      partialDrainMessage: formatMessage({id: 'scanning.total.harndess.ph'}),
    };
  }

  return {
    partialDrainVisible: false,
    partialDrainMessage: '',
  };
};

export const canadaPartialDrains = ({sanitizerType, pool, analysis, dealer, branch, formatMessage}) => {
  const naturalChemistyEnabled = !!branch && !!branch.naturalChemistry;
  const {volume} = pool;

  // [ALEX-2903] - Added a condition where the oxidizer values must have been entered before showing the popup.
  // It was possible to submit the form when selecting one of the popup options without having those values causing
  // the recommendation to be skipped.
  const hasOxidizerValues = analysis.freeChlorine && analysis.totalChlorine || analysis.totalBromine;

  if (!hasOxidizerValues) {
    return {
      partialDrainVisible: false,
      partialDrainMessage: '',
    };
  }

  if ([chemicalBaseTypes.chlorinePool, chemicalBaseTypes.brominePool, chemicalBaseTypes.synergyPool].includes(sanitizerType)) {
    if (analysis.tDS > 2500) {
      if (volume > 9500) {
        return {
          partialDrainVisible: true,
          partialDrainMessage: formatMessage({id: 'scanning.partial.drain.tds'}),
        };
      }

      return {
        partialDrainVisible: true,
        partialDrainMessage: formatMessage({id: 'scanning.complete.drain.tds'}),
      };
    }

    if (sanitizerType === chemicalBaseTypes.brominePool && analysis.cYA > 30 && !dealer.disableDrainPopBromide ||
      [chemicalBaseTypes.chlorinePool, chemicalBaseTypes.synergyPool].includes(sanitizerType) &&
        analysis.cYA > 100 && pool.property === propertyTypes.commercial ||
      [chemicalBaseTypes.chlorinePool, chemicalBaseTypes.synergyPool].includes(sanitizerType) &&
        analysis.cYA > 200 && pool.property === propertyTypes.residential) {
      const ncCYARemoverDrainEnabled = !!naturalChemistyEnabled && !!dealer.cyaRemover &&
          analysis.season !== seasonTypes.closing && pool.typeID !== systemTypes.spa;

      return {
        partialDrainVisible: true,
        ncDrainEnabled: ncCYARemoverDrainEnabled,
        partialDrainMessage: ncCYARemoverDrainEnabled ? formatMessage({id: 'scanning.nc.partial-drain.cya'}) : formatMessage({id: 'scanning.partial.drain.cya'}),
      };
    }

    if (pool.usingOptimizerPlus && volume > 9500 && analysis.optimizerPlusBorate > 90) {
      return {
        partialDrainVisible: true,
        partialDrainMessage: formatMessage({id: 'scanning.partial.drain.borate'}),
      };
    }
  }

  if ([chemicalBaseTypes.chlorineSpa, chemicalBaseTypes.bromineSpa,
    chemicalBaseTypes.synergySpa, chemicalBaseTypes.saltWaterSpa].includes(sanitizerType) && analysis.tDS > 2500) {
    return {
      partialDrainVisible: true,
      partialDrainMessage: formatMessage({id: 'scanning.complete.drain.tds'}),
    };
  }

  if ([chemicalBaseTypes.mineralSpringsPool, chemicalBaseTypes.saltWaterPool].includes(sanitizerType)) {

    if (analysis.cYA > 150 && (analysis.startup || analysis.freshFill) && pool.property === propertyTypes.residential ||
      analysis.cYA > 50 && (analysis.startup || analysis.freshFill) && pool.property === propertyTypes.commercial ||
      analysis.cYA > 200 && pool.property === propertyTypes.residential ||
      analysis.cYA > 100 && pool.property === propertyTypes.commercial) {
      const ncCYARemoverDrainEnabled = !!naturalChemistyEnabled && !!dealer.cyaRemover &&
        analysis.season !== seasonTypes.closing && pool.typeID !== systemTypes.spa &&
        pool.property !== 'C'; //Commercial pools exclusion

      return {
        partialDrainVisible: true,
        ncDrainEnabled: ncCYARemoverDrainEnabled,
        partialDrainMessage: ncCYARemoverDrainEnabled ? formatMessage({id: 'scanning.nc.partial-drain.cya'}) : formatMessage({id: 'scanning.partial.drain.cya'}),
      };
    }

    if (sanitizerType === chemicalBaseTypes.mineralSpringsPool && pool.usingOptimizerPlus &&
      analysis.optimizerPlusBorate > 50 && (analysis.startup || analysis.freshFill) ||
      pool.usingOptimizerPlus && analysis.optimizerPlusBorate > 90) {
      return {
        partialDrainVisible: true,
        partialDrainMessage: formatMessage({id: 'scanning.partial.drain.borate'}),
      };
    }

    if (pool.idealSaltPPM && analysis.minerals > pool.idealSaltPPM * 1.1) {
      return {
        partialDrainVisible: true,
        partialDrainMessage: formatMessage({id: 'scanning.partial.drain.minerals'}),
      };
    }
  }

  return {
    partialDrainVisible: false,
    partialDrainMessage: '',
  };
};

export default ({dealer, branch, analysis, pool, products, formatMessage, sanitizerType}) => {
  if (dealer.country === 'CA') {
    return canadaPartialDrains({sanitizerType, pool, products, analysis, dealer, branch, formatMessage});
  }

  return usPartialDrains({dealer, branch, sanitizerType, pool, products, analysis, formatMessage});
};
